import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaPlus, FaTimes, FaTrash, FaGripVertical, FaArrowUp, FaArrowDown, FaHeading } from 'react-icons/fa';
import './css_pages/Recipes.css';
import { getAllRecipes, createRecipe, updateRecipe, deleteRecipe, getRecipeById } from '../services/recipesService';
import { getProductsByStoreId } from '../services/productsByStoreService';
import { useStore }              from '../contexts/StoreContext';
import { getAllPreps }           from '../services/prepsService';

const ModalPortal = ({ children }) => ReactDOM.createPortal(children, document.body);

const KNOWN_UNITS_SET = new Set([
  'ml','L','L (liter)','Liter','Gallon','fl oz','fl oz (fluid ounce)','Ounce',
  'g','g (gram)','kg','kg (kilogram)','lb','lb (pound)','oz','oz (ounce)','Each'
]);

function Recipes() {
  const { selectedStore } = useStore();

  const [recipes, setRecipes]     = useState([]);
  const [products, setProducts]   = useState([]);
  const [preps, setPreps]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal]               = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showPrepModal, setShowPrepModal]       = useState(false);
  const [deleteId, setDeleteId]   = useState(null);
  const [editingId, setEditingId] = useState(null);

  // ── Form state: UNA sola lista "items" (product | section | prep) ──
  const [formData, setFormData] = useState({ posIdNumber: '', name: '', items: [] });
  const [productSearch, setProductSearch] = useState('');
  const [prepSearch, setPrepSearch]       = useState('');

  // ── Drag & drop reorder state (una sola lista unificada) ──
  const [dragIndex, setDragIndex]         = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (selectedStore) {
      fetchRecipes(selectedStore.id);
      fetchProducts(selectedStore.id);
      fetchPreps(selectedStore.id);
    }
  }, [selectedStore]);

  // ── Fetch ──
  const fetchRecipes = async (storeId) => {
    try { setLoading(true); setError(null); const r = await getAllRecipes(storeId); if (r.success) setRecipes(r.data); }
    catch { setError('Error loading recipes'); }
    finally { setLoading(false); }
  };
  const fetchProducts = async (storeId) => {
    try {
      const r = await getProductsByStoreId(storeId);
      if (r.success) setProducts(r.data.map(item => ({
        id: item.productId, name: item.productName, productCode: item.productCode,
        containerSize: item.containerSize, containerUnit: item.containerUnit,
        containerType: item.containerType, categoryName: item.categoryName || '',
        wholesalePrice: item.wholesalePrice || 0, caseSize: item.caseSize || 1
      })));
    } catch {}
  };
  const fetchPreps = async (storeId) => {
    try { const r = await getAllPreps(storeId); if (r.success) setPreps(r.data); } catch {}
  };

  // ── Unit helpers ──
  const VOLUME_TO_ML = { 'ml':1,'L':1000,'L (liter)':1000,'Liter':1000,'Gallon':3785.41,'fl oz':29.5735,'fl oz (fluid ounce)':29.5735,'Ounce':29.5735 };
  const WEIGHT_TO_G  = { 'g':1,'g (gram)':1,'kg':1000,'kg (kilogram)':1000,'lb':453.592,'lb (pound)':453.592,'oz':28.3495,'oz (ounce)':28.3495 };
  const getUnitType  = (unit) => { if (VOLUME_TO_ML[unit]) return 'volume'; if (WEIGHT_TO_G[unit]) return 'weight'; return 'count'; };
  const convertToBaseUnit = (quantity, unit, baseUnit) => {
    if (unit === baseUnit) return quantity;
    if (VOLUME_TO_ML[unit] && VOLUME_TO_ML[baseUnit]) return (quantity * VOLUME_TO_ML[unit]) / VOLUME_TO_ML[baseUnit];
    if (WEIGHT_TO_G[unit]  && WEIGHT_TO_G[baseUnit])  return (quantity * WEIGHT_TO_G[unit])  / WEIGHT_TO_G[baseUnit];
    return quantity;
  };
  const getUnitPrice = (product) => { const price = parseFloat(product.wholesalePrice)||0; const cs = parseFloat(product.caseSize)||1; return cs > 0 ? price/cs : price; };
  const calculateIngredientCost = (quantity, unit, productSize, productUnit, unitPrice) => {
    if (!unitPrice || unitPrice <= 0 || !quantity || quantity <= 0) return 0;
    const unitType = getUnitType(productUnit);
    if (unitType === 'count') return parseFloat(quantity) * unitPrice;
    const productSizeInBase = convertToBaseUnit(parseFloat(productSize)||1, productUnit, productUnit);
    const quantityInBase    = convertToBaseUnit(parseFloat(quantity), unit, productUnit);
    return (unitPrice / productSizeInBase) * quantityInBase;
  };
  const calculatePrepCost = (prep, quantity, selectedUnit) => {
    const baseCost = parseFloat(prep.prepCost)||0, baseQuantity = parseFloat(prep.baseQuantity)||1, baseUnit = prep.baseUnit||'Each';
    if (!quantity || parseFloat(quantity) <= 0) return '0.00';
    const costPerBaseUnit = baseCost / baseQuantity;
    if (selectedUnit === baseUnit) return (costPerBaseUnit * parseFloat(quantity)).toFixed(2);
    if (selectedUnit === 'Each' || baseUnit === 'Each') return (baseCost * parseFloat(quantity)).toFixed(2);
    const all = { ...VOLUME_TO_ML, ...WEIGHT_TO_G };
    if (all[selectedUnit] && all[baseUnit]) return (costPerBaseUnit * (parseFloat(quantity) * all[selectedUnit]) / all[baseUnit]).toFixed(2);
    return (costPerBaseUnit * parseFloat(quantity)).toFixed(2);
  };

  // ── Totales (derivados de la lista unificada) ──
  const calcIngCost  = () => formData.items.filter(i => i.itemType === 'product').reduce((s,i) => s + parseFloat(i.totalCost||0), 0).toFixed(2);
  const calcPrepCost = () => formData.items.filter(i => i.itemType === 'prep').reduce((s,i) => s + parseFloat(i.totalCost||0), 0).toFixed(2);
  const calcTotal    = () => (parseFloat(calcIngCost()) + parseFloat(calcPrepCost())).toFixed(2);

  const realItemsCount = () => formData.items.filter(i => i.itemType === 'product' || i.itemType === 'prep').length;

  // ── Normalizar respuesta del backend a lista unificada ──
  const normalizeItems = (d) => {
    if (Array.isArray(d.items) && d.items.length > 0) return d.items;
    return [
      ...((d.ingredients || []).map(x => ({ ...x, itemType: x.itemType || 'product' }))),
      ...((d.preparations || []).map(p => ({ ...p, itemType: 'prep' })))
    ];
  };

  // ── Form ──
  const resetForm = () => setFormData({ posIdNumber:'', name:'', items:[] });
  const handleOpenModal  = () => { resetForm(); setEditingId(null); setShowModal(true); };
  const handleCloseModal = () => { setShowModal(false); resetForm(); setEditingId(null); };
  const handleInput = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleEditModal = async (recipe) => {
    try {
      setLoading(true);
      const r = await getRecipeById(recipe.id, selectedStore.id);
      if (r.success) {
        const rawItems = normalizeItems(r.data);
        const items = rawItems.map(it => {
          if (it.itemType === 'section') {
            return { itemType: 'section', sectionLabel: it.sectionLabel || '' };
          }
          if (it.itemType === 'prep') {
            return {
              itemType: 'prep',
              prepId: it.prepId, prepName: it.prepName, prepCost: parseFloat(it.prepCost)||0,
              baseQuantity: parseFloat(it.baseQuantity)||1, baseUnit: it.baseUnit||'Each',
              quantity: it.quantity||1, unit: it.unit||it.baseUnit||'Each',
              unitType: getUnitType(it.baseUnit||'Each'), totalCost: parseFloat(it.totalCost)||0
            };
          }
          // product
          const found = products.find(p => p.id === it.productId);
          const unitPrice = found ? getUnitPrice(found) : 0;
          return {
            itemType: 'product',
            productId: it.productId, productName: it.productName,
            productSize: found?.containerSize||1, productUnit: found?.containerUnit||it.unit,
            productPrice: unitPrice, unitType: getUnitType(found?.containerUnit||it.unit),
            quantity: it.quantity, unit: it.unit, unitCost: it.unitCost, totalCost: it.totalCost
          };
        });

        setFormData({
          posIdNumber: r.data.posIdNumber || '',
          name:        r.data.name,
          items
        });
        setEditingId(recipe.id); setShowModal(true);
      }
    } catch { alert('Error loading recipe details'); }
    finally { setLoading(false); }
  };

  // ── Add product ──
  const handleOpenProductModal = () => { setShowProductModal(true); setProductSearch(''); };
  const handleSelectProduct = (product) => {
    if (formData.items.find(i => i.itemType === 'product' && i.productId === product.id)) { alert('Already added'); return; }
    const unitPrice = getUnitPrice(product);
    const totalCost = calculateIngredientCost(1, product.containerUnit, product.containerSize, product.containerUnit, unitPrice);
    setFormData(prev => ({ ...prev, items: [...prev.items, {
      itemType: 'product',
      productId: product.id, productName: product.name,
      productSize: product.containerSize, productUnit: product.containerUnit,
      productPrice: unitPrice, unitType: getUnitType(product.containerUnit),
      quantity: 1, unit: product.containerUnit, unitCost: totalCost.toFixed(2), totalCost: totalCost.toFixed(2)
    }] }));
    setShowProductModal(false);
  };

  // ── Add preparation ──
  const handleOpenPrepModal = () => { setShowPrepModal(true); setPrepSearch(''); };
  const handleSelectPrep = (prep) => {
    if (formData.items.find(i => i.itemType === 'prep' && i.prepId === prep.id)) { alert('Already added'); return; }
    const baseQuantity = prep.yieldQuantity ? parseFloat(prep.yieldQuantity) : 1;
    const baseUnit     = prep.yieldUnit || 'Each';
    const baseCost     = parseFloat(prep.totalCost) || 0;
    setFormData(prev => ({ ...prev, items: [...prev.items, {
      itemType: 'prep',
      prepId: prep.id, prepName: prep.name, prepCost: baseCost,
      baseQuantity, baseUnit, quantity: 1, unit: baseUnit,
      unitType: getUnitType(baseUnit), totalCost: (baseCost/baseQuantity).toFixed(2)
    }] }));
    setShowPrepModal(false);
  };

  // ── Add section ──
  const handleAddSection = () => {
    setFormData(prev => ({ ...prev, items: [...prev.items, { itemType: 'section', sectionLabel: '' }] }));
  };
  const handleSectionLabelChange = (index, value) => {
    setFormData(prev => {
      const list = [...prev.items];
      list[index] = { ...list[index], sectionLabel: value };
      return { ...prev, items: list };
    });
  };

  // ── Cambiar cantidad/unidad de un item (recalcula costo según tipo) ──
  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const list = [...prev.items];
      const it = { ...list[index], [field]: value };
      if (field === 'quantity' || field === 'unit') {
        if (it.itemType === 'product') {
          const qty = parseFloat(it.quantity)||0;
          const total = calculateIngredientCost(qty, it.unit, it.productSize, it.productUnit, it.productPrice);
          it.totalCost = total.toFixed(2);
          it.unitCost  = qty > 0 ? (total/qty).toFixed(2) : '0.00';
        } else if (it.itemType === 'prep') {
          it.totalCost = calculatePrepCost(it, parseFloat(it.quantity)||0, it.unit||'Each');
        }
      }
      list[index] = it;
      return { ...prev, items: list };
    });
  };

  const handleRemoveItem = (i) => setFormData(prev => ({ ...prev, items: prev.items.filter((_,idx) => idx !== i) }));

  // ── Reorder: drag & drop (desktop) ──
  const handleDragStart = (e, index) => { setDragIndex(index); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null) return;
    if (index !== dragOverIndex) setDragOverIndex(index);
  };
  const handleDrop = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) { setDragIndex(null); setDragOverIndex(null); return; }
    setFormData(prev => {
      const list = [...prev.items];
      const [moved] = list.splice(dragIndex, 1);
      list.splice(index, 0, moved);
      return { ...prev, items: list };
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

  // ── Reorder: flechas ↑/↓ (mobile) ──
  const handleMoveItem = (index, direction) => {
    setFormData(prev => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      const list = [...prev.items];
      if (newIndex < 0 || newIndex >= list.length) return prev;
      [list[index], list[newIndex]] = [list[newIndex], list[index]];
      return { ...prev, items: list };
    });
  };

  // ── Save ──
  const handleSaveRecipe = async () => {
    if (!formData.name.trim()) { alert('Recipe name is required'); return; }
    if (realItemsCount() === 0) { alert('At least one ingredient or preparation is required'); return; }
    try {
      setLoading(true);

      // Lista unificada ordenada (el índice global ES el display_order)
      const items = formData.items.map(it => {
        if (it.itemType === 'section') {
          return { itemType: 'section', sectionLabel: (it.sectionLabel || '').trim() };
        }
        if (it.itemType === 'prep') {
          return {
            itemType: 'prep',
            prepId: it.prepId, prepName: it.prepName,
            quantity: parseFloat(it.quantity), unit: it.unit || it.baseUnit || 'Each',
            baseUnit: it.baseUnit, totalCost: parseFloat(it.totalCost)
          };
        }
        return {
          itemType: 'product',
          productId: it.productId, quantity: parseFloat(it.quantity),
          unit: it.unit, unitCost: parseFloat(it.unitCost), totalCost: parseFloat(it.totalCost)
        };
      });

      // Compat con backend viejo: solo productos en ingredients, preps en preparations
      const ingredients  = items.filter(i => i.itemType === 'product');
      const preparations = items.filter(i => i.itemType === 'prep');

      const data = {
        storeId: selectedStore.id, posIdNumber: formData.posIdNumber.trim()||null, name: formData.name.trim(),
        items, ingredients, preparations
      };
      const r = editingId ? await updateRecipe(editingId, data) : await createRecipe(data);
      if (r.success) { alert(editingId ? 'Recipe updated successfully' : 'Recipe created successfully'); await fetchRecipes(selectedStore.id); handleCloseModal(); }
    } catch (err) { alert(err.response?.status === 409 ? 'A recipe with that name already exists' : 'Error saving recipe'); }
    finally { setLoading(false); }
  };

  // ── Delete ──
  const handleOpenDeleteModal  = (id) => { setDeleteId(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setDeleteId(null); };
  const handleConfirmDelete    = async () => {
    try { setLoading(true); const r = await deleteRecipe(deleteId); if (r.success) { alert('Recipe deleted successfully'); await fetchRecipes(selectedStore.id); } handleCloseDeleteModal(); }
    catch { alert('Error deleting recipe'); }
    finally { setLoading(false); }
  };

  const filteredRecipes  = recipes.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.productCode && p.productCode.toLowerCase().includes(productSearch.toLowerCase())));
  const filteredPreps    = preps.filter(p => p.name.toLowerCase().includes(prepSearch.toLowerCase()));

  // ── Unit selects ──
  const UnitSelect = ({ unitType, value, onChange }) => (
    <select value={value} onChange={onChange} disabled={loading} className="rc-input-md">
      <option value="">Select…</option>
      {unitType === 'volume' && <optgroup label="Volume"><option value="ml">ml</option><option value="L">L (liter)</option><option value="Gallon">Gallon</option><option value="fl oz">fl oz</option></optgroup>}
      {unitType === 'weight' && <optgroup label="Weight"><option value="g">g (gram)</option><option value="kg">kg (kilogram)</option><option value="lb">lb (pound)</option><option value="oz">oz (ounce)</option></optgroup>}
      {(unitType === 'count' || !unitType) && <optgroup label="Other"><option value="Each">Each</option></optgroup>}
    </select>
  );

  const PrepUnitSelect = ({ prep, value, onChange }) => {
    const isCustomBase = prep.baseUnit && !KNOWN_UNITS_SET.has(prep.baseUnit);
    return (
      <select value={value||prep.baseUnit||'Each'} onChange={onChange} disabled={loading} className="rc-input-md">
        <option value="">Select unit…</option>
        {isCustomBase && (
          <optgroup label="Custom">
            <option value={prep.baseUnit}>{prep.baseUnit}</option>
          </optgroup>
        )}
        {!isCustomBase && (prep.unitType === 'count' || !prep.unitType || prep.baseUnit === 'Each') && (
          <optgroup label="Standard"><option value="Each">Each (batch)</option></optgroup>
        )}
        {prep.unitType === 'volume' && (
          <optgroup label="Volume">
            <option value="ml">ml</option>
            <option value="L">L (liter)</option>
            <option value="Gallon">Gallon</option>
            <option value="fl oz">fl oz</option>
          </optgroup>
        )}
        {prep.unitType === 'weight' && (
          <optgroup label="Weight">
            <option value="g">g (gram)</option>
            <option value="kg">kg (kilogram)</option>
            <option value="lb">lb (pound)</option>
            <option value="oz">oz (ounce)</option>
          </optgroup>
        )}
      </select>
    );
  };

  // ── Render fila desktop (tabla unificada) ──
  const renderDesktopRow = (it, i) => {
    const dragCls = [
      dragIndex === i ? 'rc-row-dragging' : '',
      dragOverIndex === i && dragIndex !== i ? 'rc-row-drag-over' : ''
    ].filter(Boolean).join(' ');

    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleDragStart(e, i),
      onDragOver:  (e) => handleDragOver(e, i),
      onDrop:      (e) => handleDrop(e, i),
      onDragEnd:   handleDragEnd
    };

    if (it.itemType === 'section') {
      return (
        <tr key={i} className={['rc-section-row', dragCls].filter(Boolean).join(' ')} {...dragProps}>
          <td className="rc-drag-handle-cell" title="Drag to reorder"><FaGripVertical className="rc-drag-handle-icon" /></td>
          <td colSpan="3">
            <input
              type="text"
              className="rc-section-input"
              placeholder="Section title, e.g. Base, Toppings…"
              value={it.sectionLabel}
              onChange={(e) => handleSectionLabelChange(i, e.target.value)}
              disabled={loading}
            />
          </td>
          <td><button className="rc-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button></td>
        </tr>
      );
    }

    if (it.itemType === 'prep') {
      return (
        <tr key={i} className={['rc-row-prep', dragCls].filter(Boolean).join(' ')} {...dragProps}>
          <td className="rc-drag-handle-cell" title="Drag to reorder"><FaGripVertical className="rc-drag-handle-icon" /></td>
          <td>
            <div className="rc-prep-info">
              <strong>{it.prepName} <span className="rc-type-badge">pre-batch</span></strong>
              <small>{it.baseQuantity > 1 && it.baseUnit !== 'Each' ? `$${(it.prepCost/it.baseQuantity).toFixed(4)} / ${it.baseUnit}` : `$${parseFloat(it.prepCost).toFixed(2)} / batch`}</small>
            </div>
          </td>
          <td><input type="number" step="1" min="1" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="rc-input-sm" /></td>
          <td><PrepUnitSelect prep={it} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} /></td>
          <td className="rc-total-cost-cell">${parseFloat(it.totalCost).toFixed(2)}</td>
          <td><button className="rc-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button></td>
        </tr>
      );
    }

    // product
    return (
      <tr key={i} className={dragCls} {...dragProps}>
        <td className="rc-drag-handle-cell" title="Drag to reorder"><FaGripVertical className="rc-drag-handle-icon" /></td>
        <td>
          <div className="rc-product-info">
            <strong>{it.productName}</strong>
            <small>Unit price: ${parseFloat(it.productPrice).toFixed(4)} / {it.productUnit}</small>
          </div>
        </td>
        <td><input type="number" step="1" min="0" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="rc-input-sm" /></td>
        <td><UnitSelect unitType={it.unitType} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} /></td>
        <td className="rc-cost-cell">${parseFloat(it.totalCost).toFixed(2)}</td>
        <td><button className="rc-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button></td>
      </tr>
    );
  };

  // ── Render card mobile (unificada) ──
  const renderMobileCard = (it, i) => {
    const orderBtns = (
      <div className="rc-ing-card-order">
        <button type="button" className="rc-btn-order" onClick={() => handleMoveItem(i, 'up')} disabled={i === 0 || loading}><FaArrowUp /></button>
        <button type="button" className="rc-btn-order" onClick={() => handleMoveItem(i, 'down')} disabled={i === formData.items.length - 1 || loading}><FaArrowDown /></button>
      </div>
    );

    if (it.itemType === 'section') {
      return (
        <div key={i} className="rc-section-card">
          <input
            type="text"
            className="rc-section-input rc-section-input--mobile"
            placeholder="Section title, e.g. Base, Toppings…"
            value={it.sectionLabel}
            onChange={(e) => handleSectionLabelChange(i, e.target.value)}
            disabled={loading}
          />
          <div className="rc-section-card-actions">
            {orderBtns}
            <button className="rc-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button>
          </div>
        </div>
      );
    }

    if (it.itemType === 'prep') {
      return (
        <div key={i} className="rc-ing-card rc-ing-card--prep">
          <div className="rc-ing-card-header">
            <div className="rc-ing-card-name">{it.prepName} <span className="rc-type-badge">pre-batch</span></div>
            {orderBtns}
            <button className="rc-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button>
          </div>
          <div className="rc-ing-card-meta">
            {it.baseQuantity > 1 && it.baseUnit !== 'Each'
              ? `$${(it.prepCost/it.baseQuantity).toFixed(4)} / ${it.baseUnit}`
              : `$${parseFloat(it.prepCost).toFixed(2)} / batch`}
          </div>
          <div className="rc-ing-card-fields">
            <div className="rc-ing-card-field">
              <label className="rc-ing-card-label">Quantity</label>
              <input type="number" step="1" min="1" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="rc-ing-card-input" />
            </div>
            <div className="rc-ing-card-field">
              <label className="rc-ing-card-label">Unit</label>
              <PrepUnitSelect prep={it} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} />
            </div>
            <div className="rc-ing-card-field">
              <label className="rc-ing-card-label">Total Cost</label>
              <span className="rc-ing-card-cost rc-ing-card-cost--prep">${parseFloat(it.totalCost).toFixed(2)}</span>
            </div>
          </div>
        </div>
      );
    }

    // product
    return (
      <div key={i} className="rc-ing-card">
        <div className="rc-ing-card-header">
          <div className="rc-ing-card-name">{it.productName}</div>
          {orderBtns}
          <button className="rc-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button>
        </div>
        <div className="rc-ing-card-meta">Unit price: ${parseFloat(it.productPrice).toFixed(4)} / {it.productUnit}</div>
        <div className="rc-ing-card-fields">
          <div className="rc-ing-card-field">
            <label className="rc-ing-card-label">Quantity</label>
            <input type="number" step="1" min="0" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="rc-ing-card-input" />
          </div>
          <div className="rc-ing-card-field">
            <label className="rc-ing-card-label">Unit</label>
            <UnitSelect unitType={it.unitType} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} />
          </div>
          <div className="rc-ing-card-field">
            <label className="rc-ing-card-label">Cost</label>
            <span className="rc-ing-card-cost">${parseFloat(it.totalCost).toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedStore) {
    return (
      <div className="rc-page">
        <div className="rc-page-header"><h1 className="rc-page-title">Recipes</h1></div>
        <div style={{ textAlign:'center', padding:'48px 24px', color:'#94a3b8' }}>
          <p>Please select a store from the sidebar to view recipes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-page">

      {/* ── HEADER ── */}
      <div className="rc-page-header rc-sticky-header">
        <div className="rc-header-left">
          <h1 className="rc-page-title">Recipes</h1>
          {selectedStore && <span className="rc-store-badge">{selectedStore.name}</span>}
        </div>
        <button className="rc-btn-primary" onClick={handleOpenModal} disabled={loading}>
          <FaPlus /> Create new recipe
        </button>
      </div>

      {error && <div className="rc-error-msg">{error}<button onClick={() => fetchRecipes(selectedStore.id)}>Retry</button></div>}

      {/* ── SEARCH ── */}
      <div className="rc-search-section">
        <div className="rc-search-bar">
          <input type="text" placeholder="Search by name…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {/* ══ MAIN TABLE — desktop ══ */}
      <div className="rc-table-wrap rc-desktop-only">
        {loading && <div className="rc-loading">Loading…</div>}
        <table className="rc-data-table">
          <thead>
            <tr><th>POS ID</th><th>Recipe Name</th><th>Ingredients</th><th>Total Cost</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filteredRecipes.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>{loading ? 'Loading…' : 'No results found'}</td></tr>
            ) : filteredRecipes.map(recipe => (
              <tr key={recipe.id}>
                <td>{recipe.posIdNumber || '-'}</td>
                <td>{recipe.name}</td>
                <td>{recipe.ingredientCount} ingredient(s)</td>
                <td>${parseFloat(recipe.totalCost).toFixed(2)}</td>
                <td>
                  <button className="rc-btn-action" onClick={() => handleEditModal(recipe)} disabled={loading}>Edit</button>
                  <button className="rc-btn-action rc-btn-delete" onClick={() => handleOpenDeleteModal(recipe.id)} disabled={loading}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══ MAIN LIST CARDS — mobile ══ */}
      <div className="rc-list-cards rc-mobile-only">
        {loading && <div className="rc-loading">Loading…</div>}
        {!loading && filteredRecipes.length === 0 && <div className="rc-loading">No results found</div>}
        {filteredRecipes.map(recipe => (
          <div key={recipe.id} className="rc-list-card">
            <div className="rc-list-card-top">
              <span className="rc-list-card-name">{recipe.name}</span>
              {recipe.posIdNumber && <span className="rc-list-card-pos">#{recipe.posIdNumber}</span>}
            </div>
            <div className="rc-list-card-stats">
              <div className="rc-list-card-stat">
                <span className="rc-list-card-stat-label">Ingredients</span>
                <span className="rc-list-card-stat-value">{recipe.ingredientCount}</span>
              </div>
              <div className="rc-list-card-stat">
                <span className="rc-list-card-stat-label">Total Cost</span>
                <span className="rc-list-card-stat-value rc-list-card-stat-cost">${parseFloat(recipe.totalCost).toFixed(2)}</span>
              </div>
            </div>
            <div className="rc-list-card-footer">
              <button className="rc-list-card-btn rc-list-card-btn--edit" onClick={() => handleEditModal(recipe)} disabled={loading}>Edit</button>
              <button className="rc-list-card-btn rc-list-card-btn--delete" onClick={() => handleOpenDeleteModal(recipe.id)} disabled={loading}><FaTrash /></button>
            </div>
          </div>
        ))}
      </div>

      {/* ══ MODAL CREATE/EDIT ══ (no cierra al hacer click afuera) */}
      {showModal && (
        <ModalPortal>
          <div className="rc-modal-overlay">
            <div className="rc-modal rc-modal-lg" onClick={e => e.stopPropagation()}>
              <div className="rc-modal-header">
                <h2>{editingId ? 'Edit recipe' : 'Create new recipe'}</h2>
                <button className="rc-btn-close" onClick={handleCloseModal}><FaTimes /></button>
              </div>
              <div className="rc-modal-body">

                {/* Basic info */}
                <div className="rc-form-row">
                  <div className="rc-form-group">
                    <label>POS ID Number</label>
                    <input type="text" value={formData.posIdNumber} onChange={e => handleInput('posIdNumber', e.target.value)} placeholder="Enter POS ID" disabled={loading} />
                  </div>
                  <div className="rc-form-group">
                    <label>Recipe Name *</label>
                    <input type="text" value={formData.name} onChange={e => handleInput('name', e.target.value)} placeholder="Enter recipe name" disabled={loading} />
                  </div>
                </div>

                {/* ── ITEMS (tabla ÚNICA: productos + secciones + preparations) ── */}
                <div className="rc-ingredients-section">
                  <div className="rc-section-header">
                    <h3>Ingredients &amp; Pre-Batch</h3>
                  </div>
                  <p className="rc-drag-hint rc-desktop-only"><FaGripVertical className="rc-star-hint-icon" /> Drag rows by the handle to reorder</p>
                  <p className="rc-drag-hint rc-mobile-only"><FaArrowUp className="rc-star-hint-icon" /> Use the arrows to reorder</p>

                  {formData.items.length === 0
                    ? <div className="rc-empty-state">No items added yet.</div>
                    : (
                      <>
                        <div className="rc-sub-table-wrap rc-desktop-only">
                          <table className="rc-sub-table rc-ing-table">
                            <thead><tr><th className="rc-th-drag"></th><th>Item</th><th>Quantity</th><th>Unit</th><th>Cost</th><th></th></tr></thead>
                            <tbody>
                              {formData.items.map((it, i) => renderDesktopRow(it, i))}
                            </tbody>
                            <tfoot>
                              <tr className="rc-total-row">
                                <td colSpan="4" className="rc-total-label">Total:</td>
                                <td className="rc-total-cost">${calcTotal()}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <div className="rc-ing-cards rc-mobile-only">
                          {formData.items.map((it, i) => renderMobileCard(it, i))}
                          <div className="rc-ing-subtotal">Total: <strong>${calcTotal()}</strong></div>
                        </div>
                      </>
                    )}

                  {/* ── Botones: Section / Ingredient / Preparation (misma lista) ── */}
                  <div className="rc-section-add-btn-wrap">
                    <button className="rc-btn-add-section" onClick={handleAddSection} disabled={loading} type="button">
                      <FaHeading /> Add Section
                    </button>
                    <button className="rc-btn-add" onClick={handleOpenProductModal} disabled={loading} type="button">
                      <FaPlus /> Add Ingredient
                    </button>
                    <button className="rc-btn-add rc-btn-add-green" onClick={handleOpenPrepModal} disabled={loading} type="button">
                      <FaPlus /> Add Preparation
                    </button>
                  </div>
                </div>

                {/* ── TOTAL ── */}
                {formData.items.length > 0 && (
                  <div className="rc-final-total">
                    <div className="rc-final-total-inner">
                      <div>
                        <div className="rc-final-total-label">Total Recipe Cost</div>
                        <div className="rc-final-total-breakdown">Ingredients: ${calcIngCost()} + Preparations: ${calcPrepCost()}</div>
                      </div>
                      <div className="rc-final-total-value">${calcTotal()}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="rc-modal-footer">
                <button className="rc-btn-secondary" onClick={handleCloseModal} disabled={loading}>Cancel</button>
                <button className="rc-btn-primary"   onClick={handleSaveRecipe} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL SELECT PRODUCT ══ (no cierra al hacer click afuera) */}
      {showProductModal && (
        <ModalPortal>
          <div className="rc-modal-overlay">
            <div className="rc-modal rc-modal-picker" onClick={e => e.stopPropagation()}>
              <div className="rc-modal-header">
                <h2>Select Product</h2>
                <button className="rc-btn-close" onClick={() => setShowProductModal(false)}><FaTimes /></button>
              </div>
              <div className="rc-modal-body">
                <div className="rc-form-group">
                  <input type="text" placeholder="Search products…" value={productSearch} onChange={e => setProductSearch(e.target.value)} autoFocus />
                </div>
                <div className="rc-picker-list rc-desktop-only">
                  <table className="rc-picker-table">
                    <thead><tr><th>Select</th><th>Product Name</th><th>Category</th><th>Unit Price</th></tr></thead>
                    <tbody>
                      {filteredProducts.length === 0
                        ? <tr><td colSpan="4" style={{ textAlign:'center', padding:'20px', color:'#94a3b8' }}>No products found</td></tr>
                        : filteredProducts.map(product => (
                          <tr key={product.id}>
                            <td><button className="rc-btn-select" onClick={() => handleSelectProduct(product)}>✓</button></td>
                            <td>{product.name}</td>
                            <td>{product.categoryName}</td>
                            <td>${getUnitPrice(product).toFixed(4)} / {product.containerUnit}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <div className="rc-picker-mobile-list rc-mobile-only">
                  {filteredProducts.length === 0
                    ? <div className="rc-loading">No products found</div>
                    : filteredProducts.map(product => (
                      <div key={product.id} className="rc-picker-mobile-item" onClick={() => handleSelectProduct(product)}>
                        <div className="rc-picker-mobile-name">{product.name}</div>
                        <div className="rc-picker-mobile-meta">{product.categoryName} · ${getUnitPrice(product).toFixed(4)} / {product.containerUnit}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL SELECT PREP ══ (no cierra al hacer click afuera) */}
      {showPrepModal && (
        <ModalPortal>
          <div className="rc-modal-overlay">
            <div className="rc-modal rc-modal-picker" onClick={e => e.stopPropagation()}>
              <div className="rc-modal-header">
                <h2>Select Preparation</h2>
                <button className="rc-btn-close" onClick={() => setShowPrepModal(false)}><FaTimes /></button>
              </div>
              <div className="rc-modal-body">
                <div className="rc-form-group">
                  <input type="text" placeholder="Search preparations…" value={prepSearch} onChange={e => setPrepSearch(e.target.value)} autoFocus />
                </div>
                <div className="rc-picker-list rc-desktop-only">
                  <table className="rc-picker-table">
                    <thead><tr><th>Select</th><th>Preparation Name</th><th>Yield</th><th>Cost</th></tr></thead>
                    <tbody>
                      {filteredPreps.length === 0
                        ? <tr><td colSpan="4" style={{ textAlign:'center', padding:'20px', color:'#94a3b8' }}>No preparations found</td></tr>
                        : filteredPreps.map(prep => (
                          <tr key={prep.id}>
                            <td><button className="rc-btn-select" onClick={() => handleSelectPrep(prep)}>✓</button></td>
                            <td>{prep.name}</td>
                            <td>{prep.yieldQuantity && prep.yieldUnit ? `${prep.yieldQuantity} ${prep.yieldUnit}` : '—'}</td>
                            <td>${parseFloat(prep.totalCost).toFixed(2)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <div className="rc-picker-mobile-list rc-mobile-only">
                  {filteredPreps.length === 0
                    ? <div className="rc-loading">No preparations found</div>
                    : filteredPreps.map(prep => (
                      <div key={prep.id} className="rc-picker-mobile-item" onClick={() => handleSelectPrep(prep)}>
                        <div className="rc-picker-mobile-name">{prep.name}</div>
                        <div className="rc-picker-mobile-meta">
                          {prep.yieldQuantity && prep.yieldUnit ? `${prep.yieldQuantity} ${prep.yieldUnit}` : '—'} · ${parseFloat(prep.totalCost).toFixed(2)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL DELETE ══ (no cierra al hacer click afuera) */}
      {showDeleteModal && (
        <ModalPortal>
          <div className="rc-modal-overlay">
            <div className="rc-modal rc-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="rc-modal-header">
                <h2>Confirm deletion</h2>
                <button className="rc-btn-close" onClick={handleCloseDeleteModal}><FaTimes /></button>
              </div>
              <div className="rc-modal-body">
                <p style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Are you sure you want to delete this recipe?</p>
                <p className="rc-warning-text">This action cannot be undone.</p>
              </div>
              <div className="rc-modal-footer">
                <button className="rc-btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>Cancel</button>
                <button className="rc-btn-danger"    onClick={handleConfirmDelete}    disabled={loading}>{loading ? 'Deleting…' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default Recipes;